import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'
import { assertCycleDay } from '@domain/finance'
import type { CreditCard } from '@domain/finance'
import type {
  CreditCardPatch,
  CreditCardRepository,
  NewCreditCard,
} from '@domain/ports/finance-repository'
import {
  optionalCents,
  optionalText,
  requireInteger,
  requireIso,
  requireOneOf,
  textOr,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * Os cartões da casa. Mesma forma do repositório de contas — `creditCards` tem
 * `read, write` livres a membro em `firestore.rules` —, com a diferença que dá
 * ao cartão a razão de ser uma entidade à parte: `closingDay` e `dueDay`.
 *
 * **Os dois dias são cobrados aqui, com a função do domínio.**
 * `Docs/02-Dominio/CreditCard.md` diz que a faixa é 1..31 e que *"quem cobra é
 * a validação de quem escreve"* — quem escreve é este arquivo. A cobrança é
 * `assertCycleDay`, a mesma que `invoiceCycleFor` chama: repetir a faixa aqui
 * daria dois lugares para errá-la e um a mais para os dois discordarem.
 *
 * O custo de não cobrar não é um erro visível: um cartão com `closingDay: 40`
 * entra no banco calado e só explode na primeira fatura que alguém tentar
 * montar, meses depois, longe do formulário que o criou.
 */

const BRANDS = ['visa', 'mastercard', 'elo', 'amex', 'other'] as const
const PROVIDERS = ['pluggy', 'manual'] as const
const STATUSES = ['active', 'archived', 'sync_error'] as const

function cardsPath(): string {
  return `households/${HOUSEHOLD_ID}/creditCards`
}

/** Firestore → domínio. O id e o `householdId` saem do caminho. */
export function toCreditCard(id: string, data: DocumentData): CreditCard {
  const where = `creditCards/${id}`
  return {
    id,
    householdId: HOUSEHOLD_ID,
    name: textOr(data['name'], 'Cartão sem nome'),
    institution: textOr(data['institution'], ''),
    brand: requireOneOf(data['brand'], BRANDS, `${where}.brand`),
    lastFourDigits: optionalText(data['lastFourDigits']),
    creditLimitCents: optionalCents(data['creditLimitCents'], `${where}.creditLimitCents`),
    // Inteiros obrigatórios, e não `?? 1`: um dia de fechamento default poria
    // todas as compras do cartão numa fatura que não é a delas, e o número
    // sairia plausível — que é o pior jeito de errar uma data.
    closingDay: requireInteger(data['closingDay'], `${where}.closingDay`),
    dueDay: requireInteger(data['dueDay'], `${where}.dueDay`),
    paymentAccountId: optionalText(data['paymentAccountId']),
    provider: requireOneOf(data['provider'], PROVIDERS, `${where}.provider`),
    providerItemId: optionalText(data['providerItemId']),
    providerAccountId: optionalText(data['providerAccountId']),
    status: requireOneOf(data['status'], STATUSES, `${where}.status`),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

export function creditCardDocument(input: NewCreditCard): DocumentData {
  assertCycleDay(input.closingDay, 'closingDay')
  assertCycleDay(input.dueDay, 'dueDay')

  return {
    name: input.name,
    institution: input.institution,
    brand: input.brand,
    lastFourDigits: input.lastFourDigits,
    creditLimitCents: input.creditLimitCents,
    closingDay: input.closingDay,
    dueDay: input.dueDay,
    paymentAccountId: input.paymentAccountId,
    provider: 'manual',
    providerItemId: null,
    providerAccountId: null,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
}

/**
 * O patch, com o mesmo cuidado do de conta: chave ausente fica fora do objeto,
 * porque `updateDoc` lança em `undefined`.
 *
 * A cobrança dos dois dias vale igual na edição — é a mesma escrita, e mudar o
 * fechamento de um cartão existente é justamente a operação que teria como
 * gravar um 40 sobre um 25 que já funcionava.
 */
export function creditCardPatchDocument(patch: CreditCardPatch): DocumentData {
  if (patch.closingDay !== undefined) assertCycleDay(patch.closingDay, 'closingDay')
  if (patch.dueDay !== undefined) assertCycleDay(patch.dueDay, 'dueDay')

  const document: DocumentData = { updatedAt: serverTimestamp() }
  if (patch.name !== undefined) document['name'] = patch.name
  if (patch.institution !== undefined) document['institution'] = patch.institution
  if (patch.brand !== undefined) document['brand'] = patch.brand
  if (patch.lastFourDigits !== undefined) document['lastFourDigits'] = patch.lastFourDigits
  if (patch.creditLimitCents !== undefined) document['creditLimitCents'] = patch.creditLimitCents
  if (patch.closingDay !== undefined) document['closingDay'] = patch.closingDay
  if (patch.dueDay !== undefined) document['dueDay'] = patch.dueDay
  if (patch.paymentAccountId !== undefined) document['paymentAccountId'] = patch.paymentAccountId
  if (patch.status !== undefined) document['status'] = patch.status
  if (patch.providerAccountId !== undefined) {
    document['providerAccountId'] = patch.providerAccountId
    // Mesmo par do `account-repository`: id do provider com `provider:
    // 'manual'` seria mentira.
    document['provider'] = patch.providerAccountId === null ? 'manual' : 'pluggy'
  }
  return document
}

/** Sem `orderBy`, pelo mesmo motivo escrito em `listAccounts`: cartão sem
 * `name` sumiria da consulta em vez de aparecer com o texto de fallback. */
async function listCreditCards(db: Firestore): Promise<CreditCard[]> {
  const snap = await getDocs(collection(db, cardsPath()))
  return snap.docs.map((card) => toCreditCard(card.id, card.data()))
}

async function createCreditCard(db: Firestore, input: NewCreditCard): Promise<string> {
  const ref = await addDoc(collection(db, cardsPath()), creditCardDocument(input))
  return ref.id
}

async function updateCreditCard(
  db: Firestore,
  id: string,
  patch: CreditCardPatch,
): Promise<void> {
  await updateDoc(doc(db, cardsPath(), id), creditCardPatchDocument(patch))
}

/** A porta com o `db` já preso, para a composição em `src/main.tsx`. */
export function createFirebaseCreditCardRepository(db: Firestore): CreditCardRepository {
  return {
    list: () => listCreditCards(db),
    create: (input) => createCreditCard(db, input),
    update: (id, patch) => updateCreditCard(db, id, patch),
  }
}
